import { useState, useEffect } from 'react';
import { reportService } from '../api/reports';
import { useAuth } from '../auth/AuthProvider';
import StatsCard from '../components/ui/StatsCard';
import { FileText, CheckCircle, Clock, Archive, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ErrorBoundary from '../components/ErrorBoundary';
import { useNavigate } from 'react-router-dom';

export default function DashboardPage() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const data = await reportService.getDashboardStats();
                console.log("Dashboard data:", data);
                setStats(data);
            } catch (error) {
                console.error("Failed to fetch dashboard stats", error);
                setError(error.message || "Failed to load");
            } finally {
                setLoading(false);
            }
        };

        if (user?.role === 'head' || user?.role === 'vice') {
            fetchStats();
        } else {
            setError(`Role mismatch: ${user?.role}`);
            setLoading(false);
        }
    }, [user]);

    console.log("Dashboard Render. Loading:", loading, "Stats:", stats, "User:", user, "Error:", error);

    if (loading) return <div className="p-8 text-black">{t('loading_dashboard')}</div>;

    if (error) return (
        <div className="p-8 bg-red-50 text-red-600 rounded-lg">
            <h3 className="font-bold">{t('error_loading')}</h3>
            <p>{JSON.stringify(error)}</p>
            <p className="text-sm mt-2">Ваша роль: {user?.role}</p>
        </div>
    );

    if (!stats) return <div className="p-8 text-black">Нет данных (Stats is null)</div>;

    return (
        <ErrorBoundary>
            <div className="space-y-8 pb-12">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight">{t('dashboard_title')}</h1>
                    <p className="text-sm text-gray-500 mt-1">{t('dashboard_subtitle')}</p>
                </div>

                {/* Main Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <StatsCard
                        title={t('stats_total')}
                        value={stats.total}
                        icon={FileText}
                        color="blue"
                    />
                    <StatsCard
                        title={t('stats_archived')}
                        value={stats.archived}
                        icon={Archive}
                        color="green"
                    />
                    <StatsCard
                        title={t('stats_active')}
                        value={stats.pending_head + stats.pending_vice}
                        icon={Clock}
                        color="yellow"
                    />
                    <StatsCard
                        title={t('stats_rejected')}
                        value={stats.rejected}
                        icon={CheckCircle}
                        color="red"
                    />
                </div>

                {/* Progress Bar Widget */}
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">{t('all_reports_status')}</h3>
                    <div className="w-full bg-gray-100 rounded-full h-4 overflow-hidden flex">
                        <div
                            className="bg-green-500 h-full transition-all duration-1000"
                            style={{ width: `${(stats.archived / stats.total) * 100 || 0}%` }}
                            title="Archived"
                        />
                        <div
                            className="bg-purple-500 h-full transition-all duration-1000"
                            style={{ width: `${(stats.pending_vice / stats.total) * 100 || 0}%` }}
                            title="Pending Vice"
                        />
                        <div
                            className="bg-yellow-500 h-full transition-all duration-1000"
                            style={{ width: `${(stats.pending_head / stats.total) * 100 || 0}%` }}
                            title="Pending Head"
                        />
                        <div
                            className="bg-red-500 h-full transition-all duration-1000"
                            style={{ width: `${(stats.rejected / stats.total) * 100 || 0}%` }}
                            title="Rejected"
                        />
                    </div>
                    <div className="flex flex-wrap gap-4 mt-4 text-xs text-gray-500">
                        <div className="flex items-center"><div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>{t('stats_archived')} {stats.archived > 0 && `(${Math.round((stats.archived / stats.total) * 100)}%)`}</div>
                        <div className="flex items-center"><div className="w-3 h-3 bg-purple-500 rounded-full mr-2"></div>{t('stats_vice')} {stats.pending_vice > 0 && `(${Math.round((stats.pending_vice / stats.total) * 100)}%)`}</div>
                        <div className="flex items-center"><div className="w-3 h-3 bg-yellow-500 rounded-full mr-2"></div>{t('stats_head')} {stats.pending_head > 0 && `(${Math.round((stats.pending_head / stats.total) * 100)}%)`}</div>
                        <div className="flex items-center"><div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>{t('stats_rejected')} {stats.rejected > 0 && `(${Math.round((stats.rejected / stats.total) * 100)}%)`}</div>
                    </div>
                </div>

                {/* Tutor Activity Table */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                        <div>
                            <h3 className="text-lg font-bold text-gray-900">{t('tutor_activity')}</h3>
                            <p className="text-sm text-gray-500">{t('tutor_activity_subtitle')}</p>
                        </div>
                        <div className="bg-blue-50 p-2 rounded-lg text-blue-600">
                            <Users className="w-5 h-5" />
                        </div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead>
                                <tr className="bg-gray-50/50 text-xs uppercase tracking-wider text-gray-500 font-semibold border-b border-gray-100">
                                    <th className="px-6 py-4">{t('table_tutor')}</th>
                                    <th className="px-6 py-4 text-center">{t('table_total')}</th>
                                    <th className="px-6 py-4 text-center">{t('table_approved')}</th>
                                    <th className="px-6 py-4 text-center">{t('table_efficiency')}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {stats.tutor_stats && stats.tutor_stats.length > 0 ? (
                                    stats.tutor_stats.map((tutor) => {
                                        const successRate = tutor.total_reports > 0
                                            ? Math.round((tutor.approved_reports / tutor.total_reports) * 100)
                                            : 0;

                                        return (
                                            <tr
                                                key={tutor.id}
                                                className="hover:bg-gray-50/80 transition-colors cursor-pointer"
                                                onClick={() => navigate(`/tutors/${tutor.id}`)}
                                            >
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center">
                                                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-xs font-bold text-gray-600 mr-3">
                                                            {tutor.username?.[0]?.toUpperCase()}
                                                        </div>
                                                        <div>
                                                            <div className="font-medium text-gray-900">{tutor.username}</div>
                                                            <div className="text-xs text-gray-400">{tutor.first_name} {tutor.last_name}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-center font-medium text-gray-700">
                                                    {tutor.total_reports}
                                                </td>
                                                <td className="px-6 py-4 text-center font-medium text-green-600">
                                                    {tutor.approved_reports}
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${successRate >= 80 ? 'bg-green-100 text-green-800' :
                                                        successRate >= 50 ? 'bg-yellow-100 text-yellow-800' :
                                                            'bg-gray-100 text-gray-800'
                                                        }`}>
                                                        {successRate}%
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="4" className="px-6 py-8 text-center text-gray-500">
                                            {t('tutors_not_found')}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </ErrorBoundary>
    );
}
